import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import {
  Box, Container, Typography, Stepper, Step, StepLabel, Button, TextField,
  MenuItem, Stack, Paper, Divider, Chip, Alert, CircularProgress,
  IconButton, useTheme, useMediaQuery, Skeleton,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import {
  getStayPackageBySlug, listStayAvailability, createBookingMutation, createBookingMealChoiceMutation,
} from '../../graphql/farmStayQueries';
import {
  quoteStay, quoteDayVisit, formatINR, validateParty, nightsBetween, ageBands,
} from '../../utils/farmStayPricing';
import {
  daysForStay, mealPlan, requiredChoices, buildWhatsappMessage, whatsappUrl, addDays, prettyDate,
  dateClashes,
} from '../../utils/farmStayBooking';
import {
  policyOf, bookingWindow, arrivalWindowError, depositSplit, dayVisitTiers,
  CANCELLATION_TIERS, DAY_VISIT_REFUND_PERCENT,
} from '../../utils/farmStayPolicy';
import { PACKAGE_SLUG } from './FarmStayPage';
import { surface, brand, ink, line } from '../../theme/tokens';
import Seo from '../../components/Common/Seo';
import StayDatePicker from '../../components/FarmStay/StayDatePicker';
import MealChoicePicker from '../../components/FarmStay/MealChoicePicker';

const client = generateClient();

/**
 * The wizard is four steps for a stay and three for a day visit: a day visit is
 * its own product with a fixed tier, so there is no itinerary to choose meals
 * from. Steps are addressed by id rather than index, because a hardcoded
 * `step === 2` means two different screens depending on which product it is.
 */
const STEP_LABELS = {
  dates: 'Dates & guests',
  meals: 'Your meals',
  details: 'Your details',
  review: 'Review',
};

/** One card recipe, shared with the farm stay page. */
const CARD_SX = {
  bgcolor: surface.card,
  border: `1px solid ${line.subtle}`,
  borderRadius: 3,
  boxShadow: `0 1px 2px ${alpha(brand.brown, 0.05)}, 0 10px 28px ${alpha(brand.brown, 0.06)}`,
};

/** Mustard is a surface, never text on cream: dark ink sits on top of it. */
const MUSTARD_BUTTON_SX = {
  bgcolor: brand.mustard,
  color: ink.onMustard,
  fontWeight: 700,
  '&:hover': { bgcolor: darken(brand.mustard, 0.12) },
};

// WhatsApp's own brand green -- a third-party affordance, deliberately outside
// the farm palette. Dark ink on it is 8.95:1, so the label stays readable.
const WHATSAPP_GREEN = '#25D366';
// backgroundImage: 'none' is load-bearing. The theme's MuiButton.contained sets
// `background: linear-gradient(...)`, a shorthand that also sets background-image,
// so it paints straight over any bgcolor from sx -- this button rendered mustard,
// not green, until the image was cleared.
const WHATSAPP_BUTTON_SX = {
  bgcolor: WHATSAPP_GREEN,
  backgroundImage: 'none',
  color: ink.onMustard,
  fontWeight: 700,
  '&:hover': { bgcolor: darken(WHATSAPP_GREEN, 0.15), backgroundImage: 'none' },
};


const Counter = ({ label, value, onChange, min = 0, max = 9, help }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
    <Box>
      <Typography sx={{ fontWeight: 600, color: ink.primary }}>{label}</Typography>
      {help && <Typography variant="caption" sx={{ color: ink.secondary, display: 'block', mt: 0.25 }}>{help}</Typography>}
    </Box>
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton size="small" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 700, color: ink.primary }}>{value}</Typography>
      <IconButton size="small" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
        <AddIcon fontSize="small" />
      </IconButton>
    </Stack>
  </Box>
);

const FarmStayBookingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Nights already taken. Without this a guest could pick dates that are gone,
  // answer fourteen meal questions, submit, and only find out days later when
  // Ananya replied -- wasting their time and making work for her.
  const [takenNights, setTakenNights] = useState([]);
  const [adults, setAdults] = useState(2);
  // per-guest groups default to one pick for the whole party -- most families
  // eat the same thing, and making them choose per person for every meal was
  // the main friction. Switch it off to pick individually.
  // One switch for the whole stay. Previously "Surprise us" was an option on
  // every single group, so a three-night stay asked fourteen times whether you
  // wanted to be surprised. Flip this once and there is nothing left to answer.
  const [surpriseUs, setSurpriseUs] = useState(false);
  const [childAges, setChildAges] = useState([]);
  // Which product this is, chosen up front rather than inferred. It used to be
  // derived from `nights === 0`, which meant the only way to reach a day visit
  // was to start booking a stay and then notice a chip -- nobody did. `?type=day`
  // lets the farm stay page link straight into it.
  const [mode, setMode] = useState(() => (searchParams.get('type') === 'day' ? 'day' : 'stay'));
  // which day-visit tier, when the guest is not staying the night
  const [dayVisitTierId, setDayVisitTierId] = useState('');
  const [choices, setChoices] = useState({});
  const [guest, setGuest] = useState({ name: '', phone: '', email: '', dietaryNotes: '' });

  useEffect(() => {
    (async () => {
      try {
        const res = await client.graphql({ query: getStayPackageBySlug, variables: { slug: PACKAGE_SLUG } });
        const found = res.data?.listStayPackages?.items?.[0] || null;
        setPkg(found);
        // no default length: the guest picks arrival and departure themselves
        if (found?.id) {
          const av = await client.graphql({
            query: listStayAvailability, variables: { packageId: found.id },
          });
          setTakenNights((av.data?.listStayDateBlocks?.items || []).map((b) => b.date));
        }
      } catch (e) {
        console.error('Error loading package:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const nights = useMemo(() => nightsBetween(startDate, endDate), [startDate, endDate]);
  // Declared here rather than beside the other day-visit values below because
  // availability depends on which product this is.
  const isDayVisit = mode === 'day';

  const takenSet = useMemo(() => new Set(takenNights), [takenNights]);

  /** Dates in the chosen range that are already gone. */
  const clashes = useMemo(
    () => dateClashes({ startDate, endDate, takenNights, dayVisit: isDayVisit }),
    [startDate, endDate, takenNights, isDayVisit],
  );

  /** The next arrival date with a clear run of `want` nights. */
  const nextFreeFrom = useMemo(() => {
    if (!clashes.length) return null;
    const want = Math.max(1, nights);
    const cur = new Date(`${startDate}T00:00:00Z`);
    for (let attempt = 0; attempt < 180; attempt += 1) {
      cur.setUTCDate(cur.getUTCDate() + 1);
      const probe = new Date(cur);
      let ok = true;
      for (let i = 0; i < want; i += 1) {
        if (takenSet.has(probe.toISOString().slice(0, 10))) { ok = false; break; }
        probe.setUTCDate(probe.getUTCDate() + 1);
      }
      if (ok) return cur.toISOString().slice(0, 10);
    }
    return null;
  }, [clashes, startDate, nights, takenSet]);
  const allDays = useMemo(() => pkg?.itineraryDays?.items || [], [pkg]);
  const stayDays = useMemo(() => daysForStay(allDays, nights, startDate), [allDays, nights, startDate]);
  const choiceMeta = useMemo(() => requiredChoices(stayDays), [stayDays]);
  const plan = useMemo(() => mealPlan(stayDays), [stayDays]);

  const policy = useMemo(() => policyOf(pkg), [pkg]);
  const window_ = useMemo(() => bookingWindow(pkg), [pkg]);
  const windowError = useMemo(() => arrivalWindowError(pkg, startDate), [pkg, startDate]);

  // A day visit is a separate product on its own rate card, not a stay of zero
  // nights, so it is priced from its tier and skips the itinerary entirely.
  const tiers = useMemo(() => dayVisitTiers(pkg), [pkg]);
  const tier = useMemo(() => tiers.find((t) => t.id === dayVisitTierId) || null, [tiers, dayVisitTierId]);
  /** Cheapest tier, for the "from ₹X" on the chooser. Null when none are seeded. */
  const dayVisitFrom = useMemo(() => {
    const prices = tiers.map((t) => t.price).filter((p) => typeof p === 'number');
    return prices.length ? Math.min(...prices) : null;
  }, [tiers]);

  /**
   * The two products do not share a date shape: a stay needs a departure that a
   * day visit does not have. Carrying one across leaves a half-set range that
   * reads as a broken form, so each switch resets what the other one owns.
   */
  const chooseMode = useCallback((next) => {
    if (next === mode) return;
    setMode(next);
    setStep(0);
    if (next === 'day') {
      setEndDate(startDate || '');
      setChoices({});
      setSurpriseUs(false);
    } else {
      setEndDate('');
      setDayVisitTierId('');
    }
  }, [mode, startDate]);

  const guestCount = adults + childAges.length;
  // the inclusive ages guests actually say, derived once from the exclusive
  // thresholds on the package
  const bands = useMemo(() => ageBands(pkg), [pkg]);

  const quote = useMemo(() => {
    if (!pkg) return { lines: [], total: 0 };
    if (isDayVisit) return quoteDayVisit({ tier, pkg, adults, childAges });
    return quoteStay({ pkg, nights, adults, childAges });
  }, [pkg, isDayVisit, tier, nights, adults, childAges]);

  const deposit = useMemo(() => depositSplit(quote.total, pkg), [quote.total, pkg]);

  const partyError = pkg ? validateParty(pkg, adults, childAges) : null;

  // Four steps for a stay, three for a day visit -- there are no meals to
  // choose when the itinerary does not apply.
  const stepIds = useMemo(
    () => (isDayVisit ? ['dates', 'details', 'review'] : ['dates', 'meals', 'details', 'review']),
    [isDayVisit],
  );
  const current = stepIds[Math.min(step, stepIds.length - 1)];

  // Switching to a day visit from the meals step drops a step underneath the
  // guest; without this they land on Review having skipped their own details.
  useEffect(() => {
    setStep((n) => Math.min(n, stepIds.length - 1));
  }, [stepIds.length]);

  const setChoice = useCallback((key, value) => setChoices((c) => ({ ...c, [key]: value })), []);

  // One choice per course for the whole family, so a group is answered or it
  // is not. Nothing to answer at all if the kitchen is choosing.
  const unanswered = surpriseUs
    ? []
    : choiceMeta.filter((c) => c.group.required && !choices[c.key]);

  /**
   * Why the dates step is not done yet, in the guest's words. The two products
   * fail differently: a day visit is one date and cannot be half-chosen, while
   * a stay can sit with an arrival and no departure -- which is exactly the
   * state switching back from a day visit leaves behind.
   */
  const datesBlocker = () => {
    if (clashes.length) return 'Those dates are not available.';
    if (isDayVisit) {
      if (!startDate) return 'Choose the day you would like to come.';
      if (!tier) return 'Choose what your day at the farm includes.';
      return windowError || partyError || null;
    }
    if (!startDate) return 'Choose your arrival date.';
    if (!endDate) return 'Now choose the day you leave.';
    if (endDate < startDate) return 'Your departure is before your arrival.';
    // A stay of no nights is not a cheaper stay, it is a day visit. The picker
    // will not produce one, but a zero-night stay would quote at ₹0.
    if (nights <= 0) return 'A stay is at least one night — pick the day you leave.';
    return windowError || partyError || null;
  };

  // Why Continue is disabled, in words. A dead button with no explanation is
  // the single most common way a form loses someone.
  const BLOCKERS = {
    dates: datesBlocker(),
    meals: null, // the meals step shows its own "N of M chosen"
    details: guest.name.trim().length <= 1 ? 'Add your name.'
      : guest.phone.trim().length < 8 ? 'Add a WhatsApp number we can reach you on.'
        : null,
    review: null,
  };
  const stepBlocker = BLOCKERS[current];

  const VALID = {
    dates: Boolean(startDate) && Boolean(endDate) && endDate >= startDate
      && (isDayVisit ? Boolean(tier) : nights > 0)
      && !partyError && !windowError && clashes.length === 0,
    meals: unanswered.length === 0,
    details: guest.name.trim().length > 1 && guest.phone.trim().length >= 8,
    review: true,
  };
  const stepValid = VALID[current];

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const bookingNumber = `VF-${Date.now().toString(36).toUpperCase()}`;
    try {
      const res = await client.graphql({
        query: createBookingMutation,
        variables: {
          input: {
            userId: 'guest',
            bookingNumber,
            // A day visit is not a package stay. ACTIVITY already exists in the
            // enum and is what it is -- the tier goes in its own fields rather
            // than being packed into specialRequests, which is how the "Guest"
            // bug happened.
            type: isDayVisit ? 'ACTIVITY' : 'PACKAGE',
            packageId: pkg.id,
            dayVisitTierId: isDayVisit ? tier?.id || null : null,
            dayVisitTierLabel: isDayVisit ? tier?.label || null : null,
            startDate,
            endDate: nights > 0 ? endDate : startDate,
            guests: guestCount,
            adults,
            children: childAges.length,
            totalAmount: quote.total,
            status: 'PENDING',
            paymentStatus: 'PENDING',
            dietaryNotes: guest.dietaryNotes || null,
            guestName: guest.name,
            guestPhone: guest.phone,
            guestEmail: guest.email || null,
            // free text only -- name/phone/email are proper fields now.
            // Built as a list rather than `a || null + b`, which parses as
            // `a || (null + b)` and renders the literal string "null · …".
            specialRequests: [
              childAges.length ? `child ages: ${childAges.join(', ')}` : null,
            ].filter(Boolean).join(' · ') || null,
          },
        },
      });
      const bookingId = res.data?.createBooking?.id;

      // one row per pick, so the kitchen sheet is a plain query later
      // Nothing to record when the kitchen is choosing; the note goes on the
      // booking and into the WhatsApp message instead.
      const rows = [];
      if (!surpriseUs && !isDayVisit) choiceMeta.forEach((c) => {
        const v = choices[c.key];
        const opts = c.group.options?.items || [];
        const push = (optionId, guestIndex) => {
          const opt = opts.find((o) => o.id === optionId);
          if (!opt) return;
          rows.push({
            bookingId,
            dayItemId: c.dayItemId,
            optionGroupId: c.group.id,
            optionId: opt.id,
            dayIndex: c.stayDayIndex,
            slotLabel: c.slotLabel,
            groupLabel: c.group.label,
            optionLabel: opt.label,
            guestIndex: null,
            guestName: null,
            quantity: 1,
          });
        };
        push(v, null);
      });

      await Promise.all(
        rows.map((input) =>
          client.graphql({ query: createBookingMealChoiceMutation, variables: { input } })),
      );

      const message = buildWhatsappMessage({
        pkg, bookingNumber, startDate, endDate, nights,
        adults, childAges, quote, guest, choices, choiceMeta, surpriseUs,
        deposit, dayVisitTier: isDayVisit ? tier : null,
      });
      setDone({ bookingNumber, url: whatsappUrl(message) });
      window.open(whatsappUrl(message), '_blank', 'noopener');
    } catch (e) {
      console.error('Booking failed:', e);
      setSubmitError('We could not save your request. Please try again, or message us directly on WhatsApp.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    // Reserve the wizard's real height. A 190px spinner swapped for a form this
    // tall shifts everything below it -- the same footer jump that scored 0.64
    // on the itinerary page.
    return (
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Skeleton height={44} width="55%" sx={{ mb: 1 }} />
        <Skeleton height={22} width="35%" sx={{ mb: 4 }} />
        <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={520} sx={{ borderRadius: 3 }} />
      </Container>
    );
  }

  if (!pkg) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: ink.primary }}>Booking is not open yet</Typography>
        <Button href="/contact" variant="contained" sx={{ mt: 3, ...MUSTARD_BUTTON_SX }}>Contact us</Button>
      </Container>
    );
  }

  if (done) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: brand.oliveInk }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: ink.primary, mt: 2, letterSpacing: -0.3 }}>
          Request sent
        </Typography>
        <Typography sx={{ color: ink.secondary, mt: 1.5, lineHeight: 1.7, maxWidth: 460, mx: 'auto' }}>
          Your reference is <strong>{done.bookingNumber}</strong>. We have opened WhatsApp so you can send
          the details to Ananya — if it did not open, tap below. She will confirm your dates and the total.
        </Typography>
        <Button
          href={done.url} target="_blank" rel="noopener" variant="contained" size="large"
          startIcon={<WhatsAppIcon />}
          sx={{ ...WHATSAPP_BUTTON_SX, mt: 3 }}
        >
          Send on WhatsApp
        </Button>
        <Box sx={{ mt: 2 }}>
          <Button onClick={() => navigate('/farm-stay')} sx={{ color: ink.primary }}>
            Back to the farm stay
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: surface.page, minHeight: '80vh', py: { xs: 4, md: 7 } }}>
      <Seo
        title={isDayVisit ? 'Book a Day Visit at Vidhi Farm, Birbhum' : 'Book a Weekend Farm Stay in Birbhum'}
        description={isDayVisit
          ? 'Come for the day: a Bengali farm lunch and a guided farm tour, or the full day with afternoon tea and a Baul evening. Children 6 and under come free.'
          : 'Choose your dates, guests and meals for the 2-night stay at ₹3,000 per person per night. Children 6 and under stay free, and Ananya confirms on WhatsApp.'}
        path="/farm-stay/book"
      />

      <Container maxWidth="md">
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: ink.primary, letterSpacing: -0.3 }}>
          {isDayVisit ? 'Book your day visit' : 'Book your stay'}
        </Typography>
        <Typography sx={{ color: ink.secondary, mb: 3.5 }}>
          {isDayVisit ? 'A day at Vidhi Farm' : pkg.name}
        </Typography>

        <Stepper activeStep={step} alternativeLabel={!isMobile} orientation={isMobile ? 'vertical' : 'horizontal'} sx={{ mb: 4 }}>
          {stepIds.map((id) => <Step key={id}><StepLabel>{STEP_LABELS[id]}</StepLabel></Step>)}
        </Stepper>

        <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 2.5, md: 4 } }}>
          {current === 'dates' && (
            <Stack spacing={3}>
              {/* The first question, asked out loud. A day visit used to be
                  reachable only by picking a date and then spotting a chip, so
                  the product was live and effectively invisible. */}
              <Box>
                <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 15, mb: 1.25 }}>
                  Are you staying the night?
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  {[
                    { id: 'stay', label: 'Staying the night', hint: `From ${formatINR(pkg.price)} per person, per night` },
                    { id: 'day', label: 'Just for the day', hint: dayVisitFrom ? `From ${formatINR(dayVisitFrom)} per person` : 'Lunch and a farm tour' },
                  ].map((opt) => {
                    const picked = mode === opt.id;
                    return (
                      <Box
                        key={opt.id}
                        component="button"
                        type="button"
                        role="radio"
                        aria-checked={picked}
                        onClick={() => chooseMode(opt.id)}
                        sx={{
                          font: 'inherit', textAlign: 'left', cursor: 'pointer', flex: 1, minWidth: 0,
                          p: 1.75, borderRadius: 2, border: '1.5px solid',
                          borderColor: picked ? brand.mustardInk : line.default,
                          bgcolor: picked ? alpha(brand.mustard, 0.14) : surface.card,
                          '&:focus-visible': { outline: `2px solid ${brand.mustardInk}`, outlineOffset: 2 },
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 15 }}>
                          {opt.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: ink.secondary, mt: 0.25 }}>
                          {opt.hint}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              {/* Free from/to. The package is 3 days / 2 nights, but a stay is not
                  capped to it -- guests can take five days, or a month. The
                  itinerary repeats its middle day to cover whatever they pick. */}
              <StayDatePicker
                startDate={startDate}
                endDate={endDate}
                takenNights={takenNights}
                minDate={window_.earliest}
                maxDate={window_.latest}
                singleDay={isDayVisit}
                onChange={({ startDate: s, endDate: e }) => { setStartDate(s); setEndDate(e); }}
              />
              <Typography variant="caption" sx={{ color: ink.secondary, mt: -1.5, display: 'block' }}>
                We need {window_.minNoticeDays} days' notice, and take bookings up to
                {' '}{window_.maxAdvanceMonths} months ahead.
              </Typography>

              <Box>
                {/* Stay lengths only. "Day visit" used to live in here as a
                    zero-night length, which is what buried the product: it is a
                    different thing to buy, not a shorter stay. */}
                {startDate && !isDayVisit && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: ink.secondary, alignSelf: 'center', fontWeight: 600 }}>
                      Quick pick:
                    </Typography>
                    {[
                      ['1 night', 1],
                      [`${pkg?.nights || 2} nights — the package`, pkg?.nights || 2],
                      ['5 nights', 5],
                      ['A week', 7],
                    ].map(([label, n]) => (
                      <Chip
                        key={label}
                        label={label}
                        size="small"
                        variant={nights === n ? 'filled' : 'outlined'}
                        onClick={() => setEndDate(addDays(startDate, n))}
                        sx={nights === n
                          ? { bgcolor: brand.mustard, color: ink.onMustard, fontWeight: 700 }
                          : { borderColor: line.strong, color: ink.primary }}
                      />
                    ))}
                  </Stack>
                )}
                {startDate && endDate && (
                  <Typography variant="body2" sx={{ color: ink.primary, fontWeight: 600 }}>
                    {nights > 0
                      ? `${prettyDate(startDate)} → ${prettyDate(endDate)} · ${nights} night${nights === 1 ? '' : 's'}`
                      : `${prettyDate(startDate)} — a day at the farm`}
                  </Typography>
                )}
                {startDate && endDate && endDate < startDate && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    Your departure date is before your arrival.
                  </Alert>
                )}
                {windowError && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>{windowError}</Alert>
                )}
                {nights > 31 && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    That is a long stay — Ananya will work the price out with you on WhatsApp.
                  </Alert>
                )}
                {clashes.length > 0 && (
                  <Alert
                    severity="warning"
                    sx={{ mt: 1.5 }}
                    action={nextFreeFrom && (
                      <Button
                        size="small"
                        onClick={() => { setStartDate(nextFreeFrom); setEndDate(addDays(nextFreeFrom, nights)); }}
                      >
                        Use {prettyDate(nextFreeFrom)}
                      </Button>
                    )}
                  >
                    {clashes.length === 1
                      ? `${prettyDate(clashes[0])} is already taken.`
                      : `${clashes.length} of those nights are already taken (${clashes.map(prettyDate).join(', ')}).`}
                    {nextFreeFrom
                      ? ' The next opening for the same length is below.'
                      : ' Try another set of dates.'}
                  </Alert>
                )}
                {isDayVisit && (
                  <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 15 }}>
                      What would you like your day to include?
                    </Typography>
                    <Typography variant="body2" sx={{ color: ink.secondary, mb: 1.5, mt: 0.25 }}>
                      A day visit is its own thing, with its own rate — per person.
                    </Typography>
                    {tiers.length === 0 ? (
                      <Alert severity="info">
                        Day visits are priced by hand — Ananya will confirm the amount on WhatsApp.
                      </Alert>
                    ) : (
                      <Stack spacing={1.25}>
                        {tiers.map((t) => {
                          const picked = t.id === dayVisitTierId;
                          return (
                            <Box
                              key={t.id}
                              component="button"
                              type="button"
                              role="radio"
                              aria-checked={picked}
                              onClick={() => setDayVisitTierId(t.id)}
                              sx={{
                                font: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%',
                                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                p: 1.75, borderRadius: 2, border: '1.5px solid',
                                borderColor: picked ? brand.mustardInk : line.default,
                                bgcolor: picked ? alpha(brand.mustard, 0.14) : surface.card,
                                '&:focus-visible': { outline: `2px solid ${brand.mustardInk}`, outlineOffset: 2 },
                              }}
                            >
                              <Box
                                aria-hidden
                                sx={{
                                  flexShrink: 0, mt: '2px', width: 20, height: 20, borderRadius: '50%',
                                  border: '1.5px solid',
                                  borderColor: picked ? brand.mustardInk : line.strong,
                                  bgcolor: picked ? brand.mustardInk : 'transparent',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                              >
                                {picked && <CheckCircleIcon sx={{ fontSize: 14, color: surface.card }} />}
                              </Box>
                              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1}>
                                  <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 15 }}>
                                    {t.label}
                                  </Typography>
                                  <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 15, whiteSpace: 'nowrap' }}>
                                    {formatINR(t.price)}
                                    <Typography component="span" sx={{ color: ink.secondary, fontWeight: 400, fontSize: 12 }}>
                                      {' '}pp
                                    </Typography>
                                  </Typography>
                                </Stack>
                                {t.includes?.length > 0 && (
                                  <Typography sx={{ color: ink.secondary, fontSize: 13, lineHeight: 1.55, mt: 0.25 }}>
                                    {t.includes.join(' · ')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ borderColor: line.subtle }} />
              <Counter label="Adults" value={adults} onChange={setAdults} min={1} max={pkg.maxGuests || 4} />
              <Counter
                label="Children"
                help={isDayVisit
                  // same bands, but off the tier rate rather than a night rate
                  ? `${bands.freeLabel} free · ${bands.childLabel} at ${pkg.childRatePercent ?? 50}% · ${bands.adultFrom}+ full price`
                  : `${bands.freeLabel} stay free · ${bands.childLabel} at ${formatINR(pkg.childPrice)}/night · ${bands.adultFrom}+ at the adult rate`}
                value={childAges.length} min={0} max={4}
                onChange={(n) => setChildAges((prev) => (
                  n > prev.length ? [...prev, 5] : prev.slice(0, n)
                ))}
              />
              {childAges.map((age, i) => (
                <TextField
                  key={i} select size="small" label={`Child ${i + 1} age`} value={age}
                  onChange={(e) => setChildAges((prev) => prev.map((a, j) => (j === i ? Number(e.target.value) : a)))}
                  sx={{ maxWidth: 180 }}
                >
                  {Array.from({ length: 18 }, (_, n) => n).map((n) => (
                    <MenuItem key={n} value={n}>{n} years</MenuItem>
                  ))}
                </TextField>
              ))}
              <Typography variant="caption" sx={{ color: ink.secondary, display: 'block' }}>
                The farm is yours alone — no other guests. It takes {pkg.maxGuests} people
                {isDayVisit ? '.' : `, or ${pkg.maxGuests} adults with one child aged ${bands.freeLabel}.`}
              </Typography>
              {partyError && <Alert severity="warning">{partyError}</Alert>}
            </Stack>
          )}

          {current === 'meals' && (
            <>
              <MealChoicePicker
                plan={plan}
                choices={choices}
                onChoose={setChoice}
                surpriseUs={surpriseUs}
                onSurpriseUs={setSurpriseUs}
                cutoffDays={policy.mealChoiceCutoffDays}
              />
              {unanswered.length > 0 && (
                <Alert severity="info" sx={{ mt: 2.5 }}>
                  {unanswered.length} choice{unanswered.length > 1 ? 's' : ''} left — or turn on
                  {' '}“Surprise us” and let the kitchen decide.
                </Alert>
              )}
            </>
          )}

          {current === 'details' && (
            <Stack spacing={2.5}>
              <TextField label="Your name" value={guest.name} required fullWidth
                onChange={(e) => setGuest({ ...guest, name: e.target.value })} />
              <TextField label="WhatsApp number" value={guest.phone} required fullWidth
                placeholder="+91 " onChange={(e) => setGuest({ ...guest, phone: e.target.value })} />
              <TextField label="Email (optional)" type="email" value={guest.email} fullWidth
                onChange={(e) => setGuest({ ...guest, email: e.target.value })} />
              <TextField
                label="Anything we should know?" multiline rows={3} fullWidth value={guest.dietaryNotes}
                placeholder="Allergies, no onion or garlic, a birthday, arrival time…"
                onChange={(e) => setGuest({ ...guest, dietaryNotes: e.target.value })}
              />
            </Stack>
          )}

          {current === 'review' && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" sx={{ color: brand.mustardInk, fontWeight: 700, letterSpacing: 1.2 }}>Your stay</Typography>
                <Typography sx={{ fontWeight: 600, color: ink.primary, fontSize: 17 }}>
                  {nights > 0 ? `${prettyDate(startDate)} → ${prettyDate(endDate)} · ${nights} night${nights > 1 ? 's' : ''}`
                    : `${prettyDate(startDate)} · day visit`}
                </Typography>
                <Typography variant="body2" sx={{ color: ink.secondary, mt: 0.25 }}>
                  {adults} adult{adults > 1 ? 's' : ''}
                  {childAges.length
                    ? `, ${childAges.length} ${childAges.length === 1 ? 'child' : 'children'}`
                      + ` (age ${childAges.join(', ')})`
                    : ''}
                </Typography>
                {isDayVisit && tier && (
                  <Typography variant="body2" sx={{ color: ink.secondary, mt: 0.25 }}>
                    {tier.label}{tier.includes?.length ? ` — ${tier.includes.join(' · ')}` : ''}
                  </Typography>
                )}
              </Box>
              <Divider sx={{ borderColor: line.subtle }} />
              {quote.total > 0 ? (
                <Box>
                  {quote.lines.map((l) => (
                    <Box key={l.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" sx={{ color: ink.primary }}>
                        {l.label}{l.detail ? <Typography component="span" variant="caption" sx={{ color: ink.secondary, ml: 0.5 }}>({l.detail})</Typography> : null}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: ink.primary }}>
                        {l.free ? 'Free' : formatINR(l.amount)}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5, borderColor: line.subtle }} />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      bgcolor: surface.sunken,
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: ink.primary }}>Estimated total</Typography>
                    <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 19 }}>{formatINR(quote.total)}</Typography>
                  </Box>

                  {/* The 50/50 split, spelled out. A guest who only learns
                      about a deposit in the WhatsApp reply feels ambushed. */}
                  <Box sx={{ mt: 1.5, px: 2, py: 1.5, borderRadius: 2, border: `1px dashed ${line.default}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                      <Typography variant="body2" sx={{ color: ink.primary, fontWeight: 600 }}>
                        To confirm your dates ({deposit.percent}%)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: ink.primary }}>
                        {formatINR(deposit.dueNow)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                      <Typography variant="body2" sx={{ color: ink.secondary }}>
                        {isDayVisit ? 'On the day' : 'At checkout'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: ink.secondary, fontWeight: 600 }}>
                        {formatINR(deposit.dueAtCheckout)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Alert severity="info">Ananya will confirm the price with you on WhatsApp.</Alert>
              )}

              {/* Cancellation, before they commit rather than after. */}
              <Box sx={{ bgcolor: surface.sunken, borderRadius: 2, px: 2, py: 1.75 }}>
                <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 14, mb: 0.75 }}>
                  If your plans change
                </Typography>
                {isDayVisit ? (
                  <Typography variant="body2" sx={{ color: ink.secondary, lineHeight: 1.65 }}>
                    A day visit is arranged for that one day — the kitchen shops for it and the
                    artists are booked — so the advance is
                    {DAY_VISIT_REFUND_PERCENT === 0 ? ' not refundable' : ' partly refundable'}.
                    Tell us as early as you can and we will do what we can to move the date.
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {CANCELLATION_TIERS.map((t) => (
                      <Box key={t.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: ink.secondary }}>{t.label}</Typography>
                        <Typography variant="body2" sx={{ color: ink.primary, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {t.refundPercent}% back
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              {!isDayVisit && startDate && (
                <Typography variant="body2" sx={{ color: ink.secondary }}>
                  You can change your food choices until {policy.mealChoiceCutoffDays} days before
                  you arrive. After that the kitchen has already planned around them.
                </Typography>
              )}
              <Alert severity="info" icon={<WhatsAppIcon fontSize="small" />}>
                Nothing is charged online. Tapping the button saves your request and opens WhatsApp so you can
                send it to Ananya, who will confirm your dates and the total.
              </Alert>
              {submitError && <Alert severity="error">{submitError}</Alert>}
            </Stack>
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 4, pt: 3, borderTop: `1px solid ${line.subtle}` }}
          >
            <Button
              disabled={step === 0 || submitting}
              onClick={() => setStep((s) => s - 1)}
              sx={{ color: ink.secondary }}
            >
              Back
            </Button>
            {step < stepIds.length - 1 ? (
              <Stack direction="row" spacing={2} alignItems="center">
                {!stepValid && stepBlocker && (
                  <Typography variant="body2" sx={{ color: ink.secondary }}>
                    {stepBlocker}
                  </Typography>
                )}
                <Button
                  variant="contained" disabled={!stepValid} onClick={() => setStep((s) => s + 1)}
                  sx={MUSTARD_BUTTON_SX}
                >
                  Continue
                </Button>
              </Stack>
            ) : (
              <Button
                variant="contained" size="large" disabled={submitting} onClick={handleSubmit}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
                sx={WHATSAPP_BUTTON_SX}
              >
                {submitting ? 'Sending…' : 'Send request on WhatsApp'}
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default FarmStayBookingPage;
